<script setup lang="ts">
import { equals } from 'ramda';

// icons
import { faChevronRight, faHeartBroken } from '@fortawesome/pro-solid-svg-icons';
import { faList, faTh, faSearch } from '@fortawesome/pro-regular-svg-icons';

// components
import CiSkeletonFavoriteTile from '@/components/skeleton/CiSkeletonFavoriteTile.vue';
import CiSkeletonItem from '@/components/skeleton/CiSkeletonItem.vue';
import CiSkeletonPromoSlider from '@/components/skeleton/CiSkeletonPromoSlider.vue';

/********************
 * COMPOSITIONS      *
 ********************/
const favoriteService = useFavoriteService();
const favoritesContainerRef = ref<HTMLElement | null>(null);
const getRouteBaseName = useRouteBaseName();
const localePath = useLocalePath();
const route = useRoute();
const userStore = useUserStore();
const { $gettext, $pgettext } = useGettext();
const { locale } = useI18n();
const { trackProductListView } = useTracking();

/********************
 * REFS & VARS       *
 ********************/
const currentView = ref('tiles');
const favorites = shallowRef([]);
const favoritesCount = ref(0);
const isPending = ref(true);
const limit = ref(12);
const offset = ref(0);
const pageCount = ref(0);
const recommendations = ref(null);
const timeouts = shallowRef({});
const view = shallowRef({
  tiles: 'tiles',
  list: 'list',
});

const hasFavorites = computed(() => {
  return favorites.value?.length !== 0;
});

const i18nPromoHeadline = computed(() => {
  return $gettext('You might also be interested in these campsites');
});

const isTileViewActive = computed(() => {
  return currentView.value === view.value.tiles;
});

const isListViewActive = computed(() => {
  return currentView.value === view.value.list;
});

const i18nInfoTextTitle = computed(() => {
  if (!userStore.user?.profile) {
    return $gettext('You don\'t have an account yet.');
  }
  return $gettext('You\'re favorites list is empty.');
});

const i18nInfoText = computed(() => {
  if (!userStore.user?.profile) {
    return $gettext('Register to use favorites and many other functions.');
  }
  return $gettext('You have not added any campsites as a favorite.');
});

const getOffset = computed(() => {
  return route.query && route.query.offset ? route.query.offset : offset.value;
});

const currentPage = computed(() => {
  return Math.ceil(parseInt(getOffset.value) / limit.value + 1);
});

const breadcrumb = computed(() => {
  return [
    {
      name: $gettext('Favorites'),
      class: 'breadcrumb-item--chevron',
      to: null,
    },
  ];
});

const offsetIsOutOfRange = computed(() => {
  return getOffset.value > favoritesCount.value - 1;
});

const searchRoute = computed(() => {
  return localePath({ name: RBN_SEARCH });
});

const registerRoute = computed(() => {
  return localePath({ name: 'account-register', query: { ...route.query, redirect: route.path } });
});

const loginRoute = computed(() => {
  return localePath({ name: RBN_LOGIN, query: { ...route.query, redirect: route.path } });
});

/********************
 * WATCHER           *
 ********************/
watch(() => userStore.user.profile, onUserChange, { immediate: true });

watch(
  () => route.query,
  () => {
    scrollToMainContainer();
    getFavoritesFromService();
  },
);

/********************
 * HOOKS             *
 ********************/
onMounted(() => {
  // Remove the favorite when window is closed and favorite was marked for deletion
  window.addEventListener('beforeunload', removeMarkedForRemovalFavoritesWhenLeavingPage);
});

onBeforeUnmount(() => {
  removeMarkedForRemovalFavoritesWhenLeavingPage();
  window.removeEventListener('beforeunload', removeMarkedForRemovalFavoritesWhenLeavingPage);
});

/********************
 * FUNCTIONS         *
 ********************/
function onUserChange(newValue) {
  if (!newValue) {
    isPending.value = false;
  } else {
    getFavoritesFromService();
  }
  scrollToMainContainer();
}

function getFavoritesFromService(lastRemovedIndex = null) {
  isPending.value = true;

  return favoriteService
    .getFavorites(locale.value, {
      limit: limit.value,
      offset: getOffset.value,
    })
    .then((response) => {
      favorites.value = response.results;

      if (favorites.value?.length) {
        const products = [];
        const context = [];

        favorites.value.forEach((item, idx) => {
          products.push({
            id: item.camping_id.toString(),
            name: item.name,
            category: 'campsite',
            currency: 'EUR',
            price: Math.round((item.price?.price_off_season || item.price?.price1 || 0) * 100) / 100,
            position: idx + 1 + parseInt((route.query?.offset as string) || '0'),
          });

          context.push({
            schema: 'iglu:com.camparound/campinginfo_campsite/jsonschema/1-0-1',
            data: {
              booked_products: item.purchases || null,
              camping_id: item.camping_id,
              civ2_id: item.id,
              slug: item.slug,
            },
          });
        });

        trackProductListView({
          products,
          name: 'favorites',
          context,
        });
      }

      recommendations.value = response.recommendations;
      setFavoritesCount(response.count);

      if (offsetIsOutOfRange.value) {
        navigateToFavoritesPage();
      }

      if (lastRemovedIndex !== null) {
        sendFavoritesUpdatedEvent(lastRemovedIndex);
      }
    })
    .catch((error) => {
      throw createError({ statusCode: error.response.status });
    })
    .finally(() => {
      isPending.value = false;
    });
}

function timeoutIsActive(slug) {
  return !!timeouts.value[slug];
}

function setFavoritesCount(count) {
  favoritesCount.value = count;
  pageCount.value = Math.ceil(count / limit.value);
}

function changeView(view) {
  if (currentView.value === view) {
    return;
  }

  removeMarkedForRemovalFavoritesWhenLeavingPage();
  currentView.value = view;
}

function scrollToMainContainer() {
  if (favoritesContainerRef.value) {
    nextTick(() => {
      favoritesContainerRef.value!.scrollIntoView({ block: 'start' });
    });
  }
}

function startRemovingTimer({ slug, index }: { slug: string; index: number }) {
  timeouts.value = {
    ...timeouts.value,
    [slug]: setTimeout(() => removeFavoriteItem(slug, index), 4000, slug, index),
  };
}

function stopRemovingTimer({ slug }: { slug: string }) {
  clearTimeout(timeouts.value[slug]);
  const timeoutsCopy = { ...timeouts.value };
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete timeoutsCopy[slug];
  timeouts.value = timeoutsCopy;
}

function removeFavoriteItem(campsiteSlug: string, lastRemovedIndex: number | null = null) {
  const timeoutsCopy = { ...timeouts.value };
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete timeoutsCopy[campsiteSlug];
  timeouts.value = timeoutsCopy;

  userStore
    .removeFavorite(campsiteSlug)
    .then(() => {
      if (getOffset.value >= favoritesCount.value - 1) {
        navigateToPreviousPage();
      } else {
        getFavoritesFromService(lastRemovedIndex);
      }
    })
    .catch(() => {
      // A message should appear later that the removal failed
      getFavoritesFromService();
    });
}

function navigateToPreviousPage() {
  // calculate offset of previous page
  const offset = favoritesCount.value - 1 - limit.value;
  const query = offset === 0 ? {} : { offset };
  return navigateToFavoritesPage(query);
}

function navigateToFavoritesPage(query = {}) {
  const routeQuery = { ...route.query };

  for (const [key, value] of Object.entries(routeQuery)) {
    routeQuery[key] = value?.toString();
  }

  if (equals(query, routeQuery) && getRouteBaseName(route) === RBN_FAVORITES) {
    return;
  }

  return navigateTo(
    localePath({
      name: RBN_FAVORITES,
      query,
    }),
  );
}

function sendFavoritesUpdatedEvent(lastRemovedIndex?: number | null) {
  window.dispatchEvent(new CustomEvent('favorites.updated', { detail: { lastRemovedIndex } }));
}

function listItemNumber(index: number) {
  return index + 1 + (currentPage.value - 1) * limit.value;
}

function removeMarkedForRemovalFavoritesWhenLeavingPage() {
  // Remove each favorite with an active timeout
  const keys = Object.keys(timeouts.value);
  keys.forEach(slug => removeFavoriteItem(slug));
}
</script>

<template>
  <div>
    <div
      class="bg-gray-10 pt-4"
      :class="(isTileViewActive && hasFavorites) || isPending ? 'mb-[-3.125rem] pb-7' : 'mb-1'"
    >
      <div class="container">
        <div class="row favorites__breadcrumb mb-4 hidden md:block">
          <div class="col-12">
            <CiBreadcrumb :items="breadcrumb" />
          </div>
        </div>

        <div
          v-if="hasFavorites"
          ref="favoritesContainer"
          class="row mb-0 items-center md:mb-12"
        >
          <h1 class="col-12 col-md-8 lg:text-left">
            <span>{{ $gettext('Your Favorites') }}</span>
            <span
              v-if="favoritesCount"
              class="text-normal ml-2"
            >({{ favoritesCount }})</span>
          </h1>

          <div class="col-12 col-md-4 mb-1 mt-4 md:my-0">
            <div
              v-if="favoritesCount > 0"
              class="mb-4 flex justify-end sm:justify-center"
            >
              <CiButton
                tag="button"
                :variant="isTileViewActive ? 'primary' : 'secondary'"
                class="ml-0 w-1/2 rounded-r-none md:ml-4"
                data-testid="favorites--tile-view-button"
                @click="changeView(view.tiles)"
              >
                <CiAwesomeIcon
                  :icon="faTh"
                  class="icon"
                  ratio="0.6"
                />
                <span class="ml-2 whitespace-nowrap md:hidden lg:block">{{ $gettext('Tile view') }}</span>
              </CiButton>

              <CiButton
                tag="button"
                :variant="isListViewActive ? 'primary' : 'secondary'"
                class="w-1/2 rounded-l-none"
                data-testid="favorites--list-view-button"
                @click="changeView(view.list)"
              >
                <CiAwesomeIcon
                  :icon="faList"
                  class="icon"
                  ratio="0.6"
                />
                <span class="ml-2 whitespace-nowrap md:hidden lg:block">{{ $gettext('List view') }}</span>
              </CiButton>
            </div>
          </div>
        </div>

        <div
          v-if="hasFavorites && isListViewActive"
          class="row hidden pb-1 font-bold md:flex"
        >
          <div class="col-3">
            <span class="pl-1">{{ $gettext('Name') }}</span>
          </div>
          <div class="col-2 text-center font-bold">
            {{ $pgettext('Favorites list view column', 'Country') }}
          </div>
          <div class="col-2 text-center font-bold">
            {{ $pgettext('Favorites list view column', 'Region') }}
          </div>
          <div class="col-2 text-center font-bold">
            {{ $pgettext('Favorites list view column', 'Rating') }}
          </div>
          <div class="col-2 text-center font-bold">
            {{ $pgettext('Favorites list view column', 'Price') }}
          </div>
        </div>

        <div
          v-if="!hasFavorites"
          class="row mb-0 items-center md:mb-12"
        >
          <h1 class="col-12 col-md-8 mb-2 lg:text-left">
            <span>{{ $gettext('Your Favorites') }}</span>
          </h1>
        </div>
      </div>
    </div>

    <div
      v-if="isPending"
      class="container"
    >
      <template v-if="isTileViewActive">
        <div class="row mb-12">
          <div
            v-for="index in 12"
            :key="index"
            class="col-12 col-md-6 col-lg-4 favorites__tile-item mt-6"
          >
            <CiSkeletonFavoriteTile />
          </div>
        </div>
      </template>
      <template v-if="isListViewActive">
        <div class="mt-6">
          <div
            v-for="index in 10"
            :key="index"
          >
            <CiSkeletonItem class="h-20 w-full" />
          </div>
        </div>
      </template>
    </div>
    <div
      v-else
      class="container"
    >
      <div
        v-if="hasFavorites && isTileViewActive"
        class="row mt-4 md:mt-0"
      >
        <div
          v-for="(favorite, index) in favorites"
          :key="favorite.id"
          class="col-12 col-md-6 col-lg-4 favorites__tile-item mt-6"
        >
          <CiFavoriteTile
            :favorite="favorite"
            :index="index"
            :timeout-active="timeoutIsActive(favorite.slug)"
            @ci-favorite-item-remove-favorite="startRemovingTimer"
            @ci-favorite-item-undo-remove-favorite="stopRemovingTimer"
          />
        </div>
      </div>

      <div v-else-if="hasFavorites && isListViewActive">
        <div
          v-for="(favorite, index) in favorites"
          :key="favorite.id"
          class="favorites__list-item hover:bg-[#dff3d3]"
          :class="index + 1 < limit ? 'border-b border-gray-20' : null"
        >
          <CiFavoriteListItem
            :favorite="favorite"
            :number="listItemNumber(index)"
            :timeout-active="timeoutIsActive(favorite.slug)"
            @ci-favorite-item-remove-favorite="startRemovingTimer"
            @ci-favorite-item-undo-remove-favorite="stopRemovingTimer"
          />
        </div>
      </div>
      <div
        v-else-if="!userStore.user?.profile || !isPending"
        class="row justify-center text-center"
      >
        <div>
          <CiAwesomeIcon
            :icon="faHeartBroken"
            class="my-6 fill-danger"
            ratio="2.0"
          />
        </div>
        <div class="col-12 m-1 w-auto font-bold">
          {{ i18nInfoTextTitle }}
        </div>
        <div class="col-12 mb-4">
          {{ i18nInfoText }}
        </div>

        <div
          v-if="!userStore.user?.profile"
          class="col-12 mb-12 flex justify-center"
        >
          <div class="row justify-center">
            <div class="mb-4 justify-center">
              <CiButton
                tag="NuxtLink"
                data-testid="ci-register-now"
                :href="registerRoute"
              >
                {{ $gettext('Register now') }}
                <CiAwesomeIcon
                  class="ml-2"
                  :icon="faChevronRight"
                  ratio="0.6"
                />
              </CiButton>
            </div>
            <div class="w-full text-sm">
              <span class="mr-1">{{ $gettext('Already have an account?') }}</span>
              <nuxt-link :to="loginRoute">
                <span>{{ $gettext('Sign in instead!') }}</span>
              </nuxt-link>
            </div>
          </div>
        </div>
        <div
          v-else
          class="mb-12"
        >
          <CiButton
            tag="NuxtLink"
            data-testid="ci-find-campsites-now"
            :href="searchRoute"
          >
            <CiAwesomeIcon
              class="mr-2"
              :icon="faSearch"
              ratio="0.5"
            />
            {{ $pgettext('Favorites info text button', 'Find campsites now') }}
          </CiButton>
        </div>
      </div>
      <section
        v-if="hasFavorites"
        class="my-12"
      >
        <CiPagination
          v-if="pageCount > 1"
          :offset="limit"
          :current-page="currentPage"
          :max-visible-buttons="4"
          :total-pages="pageCount"
        />
      </section>
    </div>

    <!-- recommendations -->
    <CiPromoSlider
      v-if="!isPending"
      :campsites="recommendations || undefined"
    >
      <!-- headline -->
      <template #headline>
        <h2
          class="m-0 mb-4 text-center"
          v-html="i18nPromoHeadline"
        />
      </template>

      <!-- slides -->
      <template #slide="{ campsite, index }">
        <CiPromoTile
          :campsite="campsite"
          :snowplow-context="{ bannerId: 'RecommendedCampsitesSlider', zoneId: 'similar', position: index + 1 }"
        />
      </template>
    </CiPromoSlider>
    <CiSkeletonPromoSlider
      v-else
      class="my-6"
    />
  </div>
</template>

<style></style>
